import { Link, useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import MediaSelector from '../../components/MediaSelector';
import DeleteModal from '../../components/DeleteModal';
import { useAuth } from '../../context/AuthContext';
import {
    canAccessActivityMedia,
    hasActiveSubscriptionFromApi,
    isActivityFree,
} from '../../utils/subscriptionAccess';
import {
    tryOpenAppThenStore,
    tryAutoOpenAppOnMobile,
    APP_STORE_URL,
    PLAY_STORE_URL,
} from '../../utils/appStoreLinks';
import './IndividualActivity.css';

const VOICING_LABELS = {
    no: 'Ingen stemme',
    some: 'Litt stemme',
    consistent: 'Konsistent stemme',
};

const getVoicingLabel = (level) => VOICING_LABELS[level] || level || '-';

export default function IndividualActivity() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, hasPermission, user } = useAuth();
    const canManage = hasPermission('canManageActivities');
    const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
    const [subscriptionLoading, setSubscriptionLoading] = useState(false);
    const [activity, setActivity] = useState(null);
    const [isWeeklyExercise, setIsWeeklyExercise] = useState(false);
    const [isCurrentWeeklyExercise, setIsCurrentWeeklyExercise] = useState(false);
    const [weeklyCheckDone, setWeeklyCheckDone] = useState(false);
    const [playlists, setPlaylists] = useState([]);
    const [error, setError] = useState('');
    const [fetchError, setFetchError] = useState('');
    const [loading, setLoading] = useState(true);
    const [audioPlayingNorwegian, setAudioPlayingNorwegian] = useState(null);
    const [audioPlayingEnglish, setAudioPlayingEnglish] = useState(null);
    const [isPlayingNorwegian, setIsPlayingNorwegian] = useState(false);
    const [isPlayingEnglish, setIsPlayingEnglish] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    
    // Edit mode state
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({
        activityName: '',
        shortDescription: '',
        description: '',
        voicingLevel: 'some',
        levelIds: [],
        stepId: '',
        environmentId: '',
        isPublic: false,
        isFree: true,
        isLoop: false,
        minutes: '',
        audioUrl: '',
        videoUrl: '',
        tags: []
    });
    const [levels, setLevels] = useState([]);
    const [steps, setSteps] = useState([]);
    const [environments, setEnvironments] = useState([]);
    const [existingTags, setExistingTags] = useState([]);
    const [editTagInput, setEditTagInput] = useState('');
    const [editTagSuggestions, setEditTagSuggestions] = useState([]);
    const [saving, setSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const appDeepLink = isWeeklyExercise
        ? `lopeglad://weekly-exercise/${id}`
        : `lopeglad://activities/${id}`;

    const handleOpenInApp = (event) => {
        event.preventDefault();
        tryOpenAppThenStore(appDeepLink);
    };

    const getIntensityIds = (activityData) => {
        if (Array.isArray(activityData?.intensities) && activityData.intensities.length > 0) {
            return activityData.intensities
                .map((intensity) => intensity?.id)
                .filter((id) => id !== null && id !== undefined)
                .map((id) => String(id));
        }

        if (activityData?.intensity_zone_idintensity_zone) {
            return [String(activityData.intensity_zone_idintensity_zone)];
        }

        return [];
    };

    const getIntensityLabel = (activityData) => {
        if (Array.isArray(activityData?.intensities) && activityData.intensities.length > 0) {
            return activityData.intensities.map((intensity) => intensity.name).join(', ');
        }

        return activityData?.intensityName || activityData?.level_name || '-';
    };

    useEffect(() => {
        fetchActivity();
        // Cleanup audio on unmount
        return () => {
            if (audioPlayingNorwegian) {
                audioPlayingNorwegian.pause();
            }
            if (audioPlayingEnglish) {
                audioPlayingEnglish.pause();
            }
        };
    }, [id]);

    useEffect(() => {
        if (!id || canManage || loading || !weeklyCheckDone) return;
        tryAutoOpenAppOnMobile(
            isWeeklyExercise
                ? `lopeglad://weekly-exercise/${id}`
                : `lopeglad://activities/${id}`,
            `auto_open_activity_${id}`
        );
    }, [id, canManage, loading, weeklyCheckDone, isWeeklyExercise]);

    useEffect(() => {
        if (!isAuthenticated || canManage) {
            setHasActiveSubscription(false);
            setSubscriptionLoading(false);
            return;
        }

        const fetchSubscription = async () => {
            try {
                setSubscriptionLoading(true);
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans/user`,
                    { withCredentials: true }
                );
                setHasActiveSubscription(hasActiveSubscriptionFromApi(response.data));
            } catch (err) {
                console.error('Error fetching subscription:', err);
                setHasActiveSubscription(false);
            } finally {
                setSubscriptionLoading(false);
            }
        };

        fetchSubscription();
    }, [isAuthenticated, canManage]);

    useEffect(() => {
        if (!canManage) {
            setPlaylists([]);
            return;
        }

        const fetchPlaylists = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/activities/${id}/playlists`,
                    { withCredentials: true }
                );
                setPlaylists(response.data);
            } catch (err) {
                console.error('Error fetching playlists:', err);
            }
        };

        fetchPlaylists();
    }, [id, canManage]);

    const fetchDropdownData = async () => {
        try {
            const [levelsResponse, stepsResponse, environmentsResponse, tagsResponse] = await Promise.all([
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/levels`, { withCredentials: true }),
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/steps`, { withCredentials: true }),
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/environments`, { withCredentials: true }),
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/activities/tags`, { withCredentials: true })
            ]);
            setLevels(levelsResponse.data);
            setSteps(stepsResponse.data);
            setEnvironments(environmentsResponse.data);
            setExistingTags(tagsResponse.data);
        } catch (error) {
            console.error('Error fetching dropdown data:', error);
        }
    };

    const handleEdit = () => {
        if (!activity || !canManage) return;
        
        // Use full URL for preset so video/audio preview and MediaSelector work (including SAS tokens)
        // Backend returns norwegianURL / videoURL (capital URL); support both casings
        const audioUrl = activity.norwegianURL ?? activity.norwegianUrl ?? activity.englishURL ?? activity.englishUrl ?? '';
        const videoUrl = activity.videoURL ?? activity.videoUrl ?? '';
    
        setEditData({
            activityName: activity.name || '',
            shortDescription: activity.short_description || '',
            description: activity.description || '',
            voicingLevel: activity.voicing_level || 'some',
            levelIds: getIntensityIds(activity),
            stepId: activity.steps_idsteps || '',
            environmentId: activity.environment_idenvironment || '',
            isPublic: activity.isPublic === 1 || activity.isPublic === true,
            isFree: activity.isFree === 1 || activity.isFree === true,
            isLoop: activity.isLoop === 1 || activity.isLoop === true,
            minutes: activity.minutes || '',
            audioUrl,
            videoUrl,
            tags: activity.tags ? (typeof activity.tags === 'string' ? activity.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : activity.tags) : []
        });
        
        fetchDropdownData();
        setIsEditing(true);
        setError('');
        setSuccessMessage('');
    };

    const handleCancel = () => {
        setIsEditing(false);
        setError('');
        setSuccessMessage('');
        setEditTagInput('');
        setEditTagSuggestions([]);
    };

    const handleEditTagInput = (e) => {
        const value = e.target.value;
        setEditTagInput(value);
        
        if (value.trim()) {
            const filtered = existingTags
                .filter(tag => tag.name.toLowerCase().includes(value.toLowerCase()))
                .map(tag => tag.name);
            setEditTagSuggestions(filtered);
        } else {
            setEditTagSuggestions([]);
        }
    };

    const addEditTag = (tagName) => {
        if (!editData.tags.includes(tagName) && tagName.trim()) {
            setEditData({
                ...editData,
                tags: [...editData.tags, tagName.trim()]
            });
        }
        setEditTagInput('');
        setEditTagSuggestions([]);
    };

    const removeEditTag = (tagToRemove) => {
        setEditData({
            ...editData,
            tags: editData.tags.filter(tag => tag !== tagToRemove)
        });
    };

    const handleDelete = async () => {
        if (!activity) return;
        
        try {
            const response = await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/api/activities/${id}`,
                { withCredentials: true }
            );
            
            if (response.status === 200) {
                setDeleteConfirm(null);
                // Navigate back to activities list after successful deletion
                navigate('/activities');
            }
        } catch (error) {
            console.error('Error deleting activity:', error);
            setError('Kunne ikke slette aktivitet. Vennligst prøv igjen.');
            setDeleteConfirm(null);
        }
    };

    const handleUpdate = async () => {
        // Validate that either audio or video file is selected
        if (!editData.audioUrl && !editData.videoUrl) {
            setError('Enten lydfil eller videofil er påkrevd for å oppdatere en aktivitet');
            return;
        }

        setSaving(true);
        setError('');
        setSuccessMessage('');

        try {
            const response = await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/activities/${id}`,
                {
                    name: editData.activityName,
                    shortDescription: editData.shortDescription,
                    description: editData.description,
                    voicingLevel: editData.voicingLevel,
                    intensityIds: editData.levelIds,
                    idintensity_zone: editData.levelIds[0] || null,
                    idsteps: editData.stepId,
                    idenvironment: editData.environmentId || null,
                    isPublic: editData.isPublic ? 1 : 0,
                    isFree: editData.isFree ? 1 : 0,
                    isLoop: editData.isLoop ? 1 : 0,
                    minutes: editData.minutes,
                    norwegianUrl: editData.audioUrl,
                    englishUrl: editData.audioUrl,
                    videoUrl: editData.videoUrl,
                    tags: editData.tags || []
                },
                { withCredentials: true }
            );

            if (response.status === 200) {
                setSuccessMessage('Aktivitet oppdatert');
                setIsEditing(false);
                // Refresh activity data
                await fetchActivity();
                // Clear success message after 3 seconds
                setTimeout(() => setSuccessMessage(''), 3000);
            }
        } catch (error) {
            console.error('Error updating activity:', error);
            setError('Kunne ikke oppdatere aktivitet. Vennligst prøv igjen.');
        } finally {
            setSaving(false);
        }
    };

    const fetchActivity = async () => {
        try {
            setWeeklyCheckDone(false);
            const activityResponse = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/activities/${id}`,
                { withCredentials: true }
            );

            const data = activityResponse.data;
            setActivity(data);
            setIsWeeklyExercise(Boolean(data?.isWeeklyExercise));
            setIsCurrentWeeklyExercise(Boolean(data?.isCurrentWeeklyExercise));
            setFetchError('');
            setLoading(false);
            setWeeklyCheckDone(true);
        } catch (error) {
            console.error('Error fetching activity:', error);
            setIsWeeklyExercise(false);
            setIsCurrentWeeklyExercise(false);
            setFetchError('Kunne ikke laste aktivitet');
            setLoading(false);
            setWeeklyCheckDone(true);
        }
    };

    const handlePlayAudio = (url, language) => {
        if (!canPlayMedia) return;

        if (language === "norwegian") {
            if (audioPlayingNorwegian && isPlayingNorwegian) {
                audioPlayingNorwegian.pause();
                setIsPlayingNorwegian(false);
            } else {
                if (audioPlayingNorwegian) {
                    audioPlayingNorwegian.play();
                } else {
                    const audio = new Audio(url);
                    audio.addEventListener('ended', () => setIsPlayingNorwegian(false));
                    audio.play();
                    setAudioPlayingNorwegian(audio);
                }
                setIsPlayingNorwegian(true);
            }
            // Pause English audio if playing
            if (audioPlayingEnglish && isPlayingEnglish) {
                audioPlayingEnglish.pause();
                setIsPlayingEnglish(false);
            }
        } else if (language === "english") {
            if (audioPlayingEnglish && isPlayingEnglish) {
                audioPlayingEnglish.pause();
                setIsPlayingEnglish(false);
            } else {
                if (audioPlayingEnglish) {
                    audioPlayingEnglish.play();
                } else {
                    const audio = new Audio(url);
                    audio.addEventListener('ended', () => setIsPlayingEnglish(false));
                    audio.play();
                    setAudioPlayingEnglish(audio);
                }
                setIsPlayingEnglish(true);
            }
            // Pause Norwegian audio if playing
            if (audioPlayingNorwegian && isPlayingNorwegian) {
                audioPlayingNorwegian.pause();
                setIsPlayingNorwegian(false);
            }
        }
    };

    if (loading) return <div className="activity-detail-loading">Laster…</div>;
    if (fetchError) return <div className="activity-detail-error-page">{fetchError}</div>;
    if (!activity) return <div className="activity-detail-error-page">Aktivitet ikke funnet</div>;

    const hasNorwegianAudio = Boolean(activity.norwegianURL || activity.norwegianUrl);
    const hasEnglishAudio = Boolean(activity.englishURL || activity.englishUrl);
    const hasAudio = hasNorwegianAudio || hasEnglishAudio;
    const hasVideo = Boolean(activity.videoURL || activity.videoUrl);
    const activityIsFree = isActivityFree(activity);
    const canPlayMedia = canAccessActivityMedia({
        isAuthenticated,
        canBypass: canManage,
        activityIsFree,
        hasActiveSubscription,
        isWeeklyExercise,
    });
    const mediaAccessPending = !canManage && !weeklyCheckDone;
    const subscriptionProfileUrl = user?.id ? `/users/${user.id}` : '/login';

    const renderMediaGate = (loggedOutMessage, subscriptionMessage) => {
        if (mediaAccessPending) {
            return (
                <div className="activity-detail-login-prompt">
                    <p>Sjekker tilgang…</p>
                </div>
            );
        }

        if (!isAuthenticated) {
            return (
                <div className="activity-detail-login-prompt">
                    <p>{loggedOutMessage}</p>
                    <div className="activity-detail-login-links">
                        <Link to="/login" className="primary">Logg inn</Link>
                        <Link to="/create-account" className="secondary">Opprett konto</Link>
                    </div>
                </div>
            );
        }

        if (subscriptionLoading) {
            return (
                <div className="activity-detail-login-prompt">
                    <p>Sjekker abonnement…</p>
                </div>
            );
        }

        if (!canPlayMedia) {
            return (
                <div className="activity-detail-login-prompt">
                    <p>{subscriptionMessage}</p>
                    <div className="activity-detail-login-links">
                        <Link to={subscriptionProfileUrl} className="primary">Gå til abonnement</Link>
                    </div>
                </div>
            );
        }

        return null;
    };

    return (
        <div className="activity-detail">
            <div className="activity-detail-container">
                <div className="activity-detail-top">
                    <Link to="/activities" className="activity-detail-back">
                        <i className="fas fa-arrow-left" aria-hidden="true" />
                        Tilbake til aktiviteter
                    </Link>
                    {canManage && !isEditing && (
                        <div className="activity-detail-actions">
                            <button type="button" onClick={handleEdit} className="activity-detail-btn activity-detail-btn--edit">
                                <i className="fas fa-edit" aria-hidden="true" />
                                Rediger
                            </button>
                            <button
                                type="button"
                                onClick={() => setDeleteConfirm(activity)}
                                className="activity-detail-btn activity-detail-btn--delete"
                            >
                                <i className="fas fa-trash-alt" aria-hidden="true" />
                                Slett
                            </button>
                        </div>
                    )}
                </div>

                {error && <div className="activity-detail-alert activity-detail-alert--error">{error}</div>}
                {successMessage && <div className="activity-detail-alert activity-detail-alert--success">{successMessage}</div>}

                <section className="activity-detail-hero">
                    <div className="activity-detail-hero-header">
                        <h1>{activity.name}</h1>
                        <a
                            href={appDeepLink}
                            className="activity-detail-open-app"
                            onClick={handleOpenInApp}
                        >
                            <i className="fas fa-mobile-alt" aria-hidden="true" />
                            Åpne i appen
                        </a>
                    </div>
                    {activity.short_description && (
                        <p className="activity-detail-hero-lead">{activity.short_description}</p>
                    )}
                    <div className="activity-detail-meta">
                        {isCurrentWeeklyExercise ? (
                            <span className="activity-detail-pill activity-detail-pill--weekly">
                                <i className="fas fa-calendar-week" aria-hidden="true" />
                                Ukens gratis økt
                            </span>
                        ) : isWeeklyExercise ? (
                            <span className="activity-detail-pill activity-detail-pill--weekly">
                                <i className="fas fa-calendar-week" aria-hidden="true" />
                                Dette var ukens gratis økt
                            </span>
                        ) : null}
                        {activity.isPublic ? (
                            <span className="activity-detail-pill activity-detail-pill--published">
                                <i className="fas fa-globe" aria-hidden="true" />
                                Publisert
                            </span>
                        ) : (
                            <span className="activity-detail-pill activity-detail-pill--draft">
                                <i className="fas fa-lock" aria-hidden="true" />
                                Utkast
                            </span>
                        )}
                        {activity.isFree ? (
                            <span className="activity-detail-pill">
                                <i className="fas fa-gift" aria-hidden="true" />
                                Gratis
                            </span>
                        ) : (
                            <span className="activity-detail-pill activity-detail-pill--premium">
                                <i className="fas fa-star" aria-hidden="true" />
                                Premium
                            </span>
                        )}
                        {activity.minutes && (
                            <span className="activity-detail-pill">
                                <i className="fas fa-clock" aria-hidden="true" />
                                {activity.minutes} min
                            </span>
                        )}
                    </div>
                </section>

                {canManage && isEditing ? (
                    <section className="activity-detail-section">
                        <h3>Rediger aktivitet</h3>
                                
                                <div className="form-group">
                                    <label>Aktivitetsnavn</label>
                                    <input
                                        type="text"
                                        value={editData.activityName}
                                        onChange={(e) => setEditData({ ...editData, activityName: e.target.value })}
                                        className="form-input"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Kort beskrivelse</label>
                                    <textarea
                                        value={editData.shortDescription}
                                        onChange={(e) => setEditData({ ...editData, shortDescription: e.target.value })}
                                        className="form-textarea"
                                        rows="2"
                                        maxLength="150"
                                        placeholder="Kort beskrivelse (maks 150 tegn)"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Full beskrivelse</label>
                                    <textarea
                                        value={editData.description}
                                        onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                                        className="form-textarea"
                                        rows="4"
                                        placeholder="Detaljert beskrivelse..."
                                    />
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Intensitetsnivå</label>
                                        <select
                                            value={editData.levelIds}
                                            onChange={(e) => setEditData({
                                                ...editData,
                                                levelIds: Array.from(e.target.selectedOptions).map((option) => option.value)
                                            })}
                                            className="form-select"
                                            multiple
                                        >
                                            {levels.map(level => (
                                                <option key={level.idintensity_zone} value={level.idintensity_zone}>
                                                    {level.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Steg</label>
                                        <select
                                            value={editData.stepId || ''}
                                            onChange={(e) => setEditData({ ...editData, stepId: e.target.value })}
                                            className="form-select"
                                        >
                                            <option value="">Velg et steg</option>
                                            {steps.map(step => (
                                                <option key={step.idsteps} value={step.idsteps}>
                                                    {step.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Miljø</label>
                                        <select
                                            value={editData.environmentId || ''}
                                            onChange={(e) => setEditData({ ...editData, environmentId: e.target.value })}
                                            className="form-select"
                                        >
                                            <option value="">Velg et miljø</option>
                                            {environments.map(env => (
                                                <option key={env.idenvironment} value={env.idenvironment}>
                                                    {env.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Varighet (minutter)</label>
                                        <input
                                            type="number"
                                            value={editData.minutes}
                                            onChange={(e) => setEditData({ ...editData, minutes: e.target.value })}
                                            className="form-input"
                                            min="1"
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Stemmenivå</label>
                                    <select
                                        value={editData.voicingLevel}
                                        onChange={(e) => setEditData({ ...editData, voicingLevel: e.target.value })}
                                        className="form-select"
                                    >
                                        <option value="no">Ingen stemme</option>
                                        <option value="some">Litt stemme</option>
                                        <option value="consistent">Konsistent stemme</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Tagger</label>
                                    <div className="tags-input-container">
                                        <div className="tags-list">
                                            {editData.tags.map(tag => (
                                                <span key={tag} className="tag">
                                                    {tag}
                                                    <button
                                                        type="button"
                                                        onClick={() => removeEditTag(tag)}
                                                        className="tag-remove"
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                        <input
                                            type="text"
                                            value={editTagInput}
                                            onChange={handleEditTagInput}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addEditTag(editTagInput);
                                                }
                                            }}
                                            placeholder="Legg til tagger..."
                                            className="form-input"
                                        />
                                        {editTagSuggestions.length > 0 && (
                                            <div className="tag-suggestions">
                                                {editTagSuggestions.map(suggestion => (
                                                    <div
                                                        key={suggestion}
                                                        className="tag-suggestion"
                                                        onClick={() => addEditTag(suggestion)}
                                                    >
                                                        {suggestion}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="form-group toggle-group">
                                    <label className="toggle-label">
                                        <span className="toggle-text">Publisert aktivitet</span>
                                        <div className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={editData.isPublic}
                                                onChange={(e) => setEditData({ ...editData, isPublic: e.target.checked })}
                                            />
                                            <span className="toggle-slider"></span>
                                        </div>
                                    </label>

                                    <label className="toggle-label">
                                        <span className="toggle-text">Gratis aktivitet</span>
                                        <div className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={editData.isFree}
                                                onChange={(e) => setEditData({ ...editData, isFree: e.target.checked })}
                                            />
                                            <span className="toggle-slider"></span>
                                        </div>
                                    </label>

                                    <label className="toggle-label">
                                        <span className="toggle-text">Gjentakende aktivitet</span>
                                        <div className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={editData.isLoop}
                                                onChange={(e) => setEditData({ ...editData, isLoop: e.target.checked })}
                                            />
                                            <span className="toggle-slider"></span>
                                        </div>
                                    </label>
                                </div>

                                <div className="form-group">
                                    <label>Lydfil: <span className="required">*</span></label>
                                    {editData.audioUrl && (
                                        <div className="audio-preview-edit" style={{ marginBottom: '1rem' }}>
                                            <audio
                                                controls
                                                src={editData.audioUrl}
                                                style={{ maxWidth: '100%', borderRadius: '8px' }}
                                            >
                                                Nettleseren din støtter ikke lydspiller.
                                            </audio>
                                            <small className="text-muted" style={{ display: 'block', marginTop: '0.25rem' }}>
                                                Valgt lydfil
                                            </small>
                                            <button
                                                type="button"
                                                onClick={() => setEditData({ ...editData, audioUrl: '' })}
                                                className="cancel-button"
                                                style={{ marginTop: '0.5rem' }}
                                            >
                                                <i className="fas fa-trash-alt"></i> Fjern lydfil
                                            </button>
                                        </div>
                                    )}
                                    <MediaSelector 
                                        onSelect={(url) => setEditData({ ...editData, audioUrl: url })}
                                        selectedUrl={editData.audioUrl}
                                    />
                                    {editData.audioUrl && (
                                        <small className="form-hint">Velg en annen lydfil ovenfor for å erstatte.</small>
                                    )}
                                    {!editData.audioUrl && !editData.videoUrl && (
                                        <small className="error-text">Enten lydfil eller videofil er påkrevd</small>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label>Videofil: <span className="required">*</span></label>
                                    {editData.videoUrl && (
                                        <div className="video-container-activity" style={{ marginBottom: '1rem' }}>
                                            <video
                                                controls
                                                src={editData.videoUrl}
                                                className="activity-video"
                                                style={{ maxWidth: '100%', borderRadius: '8px' }}
                                            >
                                                Nettleseren din støtter ikke video-taggen.
                                            </video>
                                            <button
                                                type="button"
                                                onClick={() => setEditData({ ...editData, videoUrl: '' })}
                                                className="cancel-button"
                                                style={{ marginTop: '0.5rem' }}
                                            >
                                                <i className="fas fa-trash-alt"></i> Fjern video
                                            </button>
                                        </div>
                                    )}
                                    <MediaSelector 
                                        onSelect={(url) => setEditData({ ...editData, videoUrl: url })}
                                        selectedUrl={editData.videoUrl}
                                        folder="video"
                                        accept="video/*"
                                    />
                                    {editData.videoUrl && (
                                        <small className="form-hint">Velg en annen video ovenfor for å erstatte.</small>
                                    )}
                                    {!editData.audioUrl && !editData.videoUrl && (
                                        <small className="error-text">Enten lydfil eller videofil er påkrevd</small>
                                    )}
                                </div>

                                <div className="form-actions">
                                    <button 
                                        onClick={handleUpdate}
                                        disabled={saving || (!editData.audioUrl && !editData.videoUrl)}
                                        className="save-button"
                                    >
                                        {saving ? 'Lagrer...' : 'Lagre endringer'}
                                    </button>
                                    <button 
                                        onClick={handleCancel}
                                        disabled={saving}
                                        className="cancel-button"
                                    >
                                        Avbryt
                                    </button>
                                </div>
                    </section>
                ) : (
                    <>
                        <section className="activity-detail-section">
                            <h3>Aktivitetsinformasjon</h3>
                            <div className="activity-detail-info-grid">
                                <div className="activity-detail-info-item">
                                    <i className="fas fa-clock" aria-hidden="true" />
                                    <span>Varighet: {activity.minutes} minutter</span>
                                </div>
                                <div className="activity-detail-info-item">
                                    <i className="fas fa-running" aria-hidden="true" />
                                    <span>Steg-nivå: {activity.stepsName || '-'}</span>
                                </div>
                                <div className="activity-detail-info-item">
                                    <i className="fas fa-mountain" aria-hidden="true" />
                                    <span>Miljø: {activity.environmentName || '-'}</span>
                                </div>
                                <div className="activity-detail-info-item">
                                    <i className="fas fa-signal" aria-hidden="true" />
                                    <span>Intensitets-soner: {getIntensityLabel(activity)}</span>
                                </div>
                                <div className="activity-detail-info-item">
                                    <i className="fas fa-volume-up" aria-hidden="true" />
                                    <span>Stemmenivå: {getVoicingLabel(activity.voicing_level)}</span>
                                </div>
                            </div>
                        </section>

                        {activity.description && (
                            <section className="activity-detail-section">
                                <h3>Beskrivelse</h3>
                                <p className="activity-detail-desc">{activity.description}</p>
                            </section>
                        )}
                    </>
                )}

                {hasAudio && (
                    <section className="activity-detail-section">
                        <h3>Lyd</h3>
                        {canPlayMedia && !mediaAccessPending ? (
                            <div className="activity-detail-audio-grid">
                                {hasNorwegianAudio && (
                                    <button
                                        type="button"
                                        className={`activity-detail-audio-btn ${isPlayingNorwegian ? 'is-playing' : ''}`}
                                        onClick={() => handlePlayAudio(activity.norwegianURL || activity.norwegianUrl, 'norwegian')}
                                    >
                                        <i className={`fas ${isPlayingNorwegian ? 'fa-pause' : 'fa-play'}`} aria-hidden="true" />
                                        {isPlayingNorwegian ? 'Pause norsk versjon' : 'Spill norsk versjon'}
                                    </button>
                                )}
                                {/* {hasEnglishAudio && (
                                    <button
                                        type="button"
                                        className={`activity-detail-audio-btn ${isPlayingEnglish ? 'is-playing' : ''}`}
                                        onClick={() => handlePlayAudio(activity.englishURL || activity.englishUrl, 'english')}
                                    >
                                        <i className={`fas ${isPlayingEnglish ? 'fa-pause' : 'fa-play'}`} aria-hidden="true" />
                                        {isPlayingEnglish ? 'Pause engelsk versjon' : 'Spill engelsk versjon'}
                                    </button>
                                )} */}
                            </div>
                        ) : (
                            renderMediaGate(
                                'Logg inn for å lytte til denne aktiviteten.',
                                activityIsFree
                                    ? 'Du har ikke tilgang til å lytte akkurat nå.'
                                    : 'Du trenger et aktivt abonnement for å lytte til denne aktiviteten.'
                            )
                        )}
                    </section>
                )}

                {hasVideo && (
                    <section className="activity-detail-section">
                        <h3>Video</h3>
                        {canPlayMedia && !mediaAccessPending ? (
                            <div className="activity-detail-video-wrap">
                                <video controls src={activity.videoURL || activity.videoUrl}>
                                    Nettleseren din støtter ikke video-taggen.
                                </video>
                            </div>
                        ) : (
                            renderMediaGate(
                                'Logg inn for å se videoen til denne aktiviteten.',
                                activityIsFree
                                    ? 'Du har ikke tilgang til å se videoen akkurat nå.'
                                    : 'Du trenger et aktivt abonnement for å se videoen til denne aktiviteten.'
                            )
                        )}
                    </section>
                )}

                {canManage && (
                    <section className="activity-detail-section">
                        <h3>Tilknyttede spillelister</h3>
                        <div className="activity-detail-playlists">
                            {playlists.length > 0 ? (
                                playlists.map((playlist) => (
                                    <div key={playlist.idplaylists} className="activity-detail-playlist-card">
                                        <h4>{playlist.name}</h4>
                                        <span>
                                            <i className="fas fa-clock" aria-hidden="true" /> {playlist.duration} minutter
                                        </span>
                                    </div>
                                ))
                            ) : (
                                <p className="activity-detail-empty">Ingen spillelister tilknyttet denne aktiviteten</p>
                            )}
                        </div>
                    </section>
                )}

                <section className="activity-detail-app-cta" aria-label="Åpne økten i appen">
                    <p className="activity-detail-app-cta-teaser">
                        Klar for økten? Åpne den i Løpeglad-appen.
                    </p>
                    <a
                        href={appDeepLink}
                        className="activity-detail-open-app activity-detail-open-app--lg"
                        onClick={handleOpenInApp}
                    >
                        <i className="fas fa-mobile-alt" aria-hidden="true" />
                        Åpne i appen
                    </a>
                    <p className="activity-detail-store-hint">
                        Har du ikke appen installert? Last ned på{' '}
                        <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">App Store</a>
                        {' '}eller{' '}
                        <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">Google Play</a>.
                    </p>
                </section>
            </div>

            {deleteConfirm && (
                <DeleteModal
                    isOpen={!!deleteConfirm}
                    onClose={() => setDeleteConfirm(null)}
                    onConfirm={handleDelete}
                    itemName={`aktiviteten "${deleteConfirm.name}"`}
                />
            )}
        </div>
    );
}